import { useEffect, useRef, useState, useCallback } from "react";
import { AudioSession, type AudioSource } from "../audio/audioSession";
import { createPerformanceAudioContext } from "../audio/capture";
export type { AudioSource, AudioSourceType } from "../audio/audioSession";

export interface UseAudioAnalyzerOptions { fftSize?: number; audioSource?: AudioSource; lookAhead?: number; }
export type AudioAnalyzer = ReturnType<typeof useAudioAnalyzer>;
const EMPTY = new Float32Array(0);

export function useAudioAnalyzer({ fftSize = 8192, audioSource, lookAhead = 0 }: UseAudioAnalyzerOptions = {}) {
  const session = useRef<AudioSession | null>(null);
  const [, render] = useState(0);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  useEffect(() => {
    if (!audioSource) return;
    let active = true;
    let context: AudioContext;
    let current: AudioSession;
    setInitializationError(null);
    try {
      context = audioSource.audioContext ?? createPerformanceAudioContext();
      current = new AudioSession(context, audioSource, fftSize, lookAhead,
        () => { if (active) render(version => version + 1); });
    } catch (error) {
      setInitializationError(error instanceof Error ? error.message : "无法启动音频。");
      return;
    }
    session.current = current;
    void current.load();
    // iOS can interrupt the context when a call, lock screen or another tab takes focus.
    const recover = () => { if (!document.hidden) void current.resume(); };
    document.addEventListener("visibilitychange", recover);
    window.addEventListener("pageshow", recover);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", recover);
      window.removeEventListener("pageshow", recover);
      current.dispose();
      if (session.current === current) session.current = null;
      if (!audioSource.audioContext && context.state !== "closed") void context.close().catch(() => {});
    };
  }, [audioSource, fftSize]);
  useEffect(() => { session.current?.setLookAhead(lookAhead); }, [lookAhead]);

  const play = useCallback(() => { void session.current?.play(); }, []);
  const resume = useCallback(() => { void session.current?.resume(); }, []);
  const pause = useCallback(() => session.current?.pause(), []);
  const stop = useCallback(() => session.current?.stop(), []);
  const seek = useCallback((time: number) => session.current?.seek(time), []);
  const getCurrentTime = useCallback(() => session.current?.getCurrentTime() ?? 0, []);
  const getDuration = useCallback(() => session.current?.getDuration() ?? 0, []);
  const getFrequencyData = useCallback(() => session.current?.getFrequencyData() ?? EMPTY, []);
  const getTimeDomainData = useCallback(() => session.current?.getTimeDomainData() ?? EMPTY, []);
  const setLookAhead = useCallback((ms: number) => session.current?.setLookAhead(ms), []);
  const stopCapture = useCallback(() => session.current?.stopCapture(), []);
  return { play, resume, pause, stop, seek, getCurrentTime, getDuration, getFrequencyData, getTimeDomainData,
    setLookAhead, stopCapture, isPlaying: session.current?.isPlaying ?? false,
    isLoading: initializationError ? false : (audioSource ? session.current?.loading ?? true : false),
    error: initializationError ?? session.current?.error ?? null,
    needsResume: session.current?.needsResume ?? false, inputMuted: session.current?.inputMuted ?? false,
    audioContext: session.current?.context ?? null, sampleRate: session.current?.context.sampleRate ?? 44100,
    fftSize, sourceType: audioSource?.type ?? null };
}
